import { cn } from "@ondit/ui";
import { ArrowLeft, ArrowRight, PanelLeft } from "@ondit/ui/icons";
import { type ReactNode, useEffect, useLayoutEffect, useState } from "react";
import { useLocation } from "react-router";
import { useSidebarStore } from "@/stores/sidebar/store";
import { useT } from "~/core/i18n";
import { SidebarChromeProvider } from "~/shell/chrome/sidebar-chrome-context";
import { useSidebarPeek } from "~/shell/chrome/use-sidebar-peek";

/**
 * The peek card's floating chrome.
 *
 * Every value is an existing token: `rounded-lg` is `--radius`, matching the content
 * pane it floats beside; `--border` is that pane's border; `--z-modal` is what the
 * app's other edge-anchored panels use. The card's fill is the sidebar's own
 * `--surface-1`, so docked and floating are the same surface.
 *
 * Deliberately unshadowed. It separates on the same `--border` hairline the content
 * pane beside it uses; `--shadow-overlay` reads as too heavy at this size, where the
 * card abuts the window edge rather than floating over the middle of the page.
 *
 * The card hugs its content and caps at the pane height less the lane and the bottom
 * gutter — pinning both edges left a tall empty slab below short lists. It is a flex
 * column so the shell can shrink inside that cap and the sidebar's own scroll region
 * still bounds itself; see the `[data-peek]` rule in `globals.css`.
 *
 * `w-auto` shrink-wraps the inner shell, which `[data-peek]` has already put at the
 * expanded width. It must not be a length: `width` cannot interpolate to or from
 * `auto`, so entering and leaving the peek snap instead of animating — otherwise the
 * card widens as it appears and leaves a shrinking ghost on retract.
 */
const PEEK_CARD_CHROME =
	"absolute top-[var(--desktop-title-bar-height)] left-2 z-[var(--z-modal)] flex max-h-[calc(100%-var(--desktop-title-bar-height)-8px)] w-auto flex-col origin-top-left rounded-lg border border-[var(--border)]";

/**
 * Peek card enter/exit — the popper idiom rather than a slide, since the card is
 * anchored to the title-bar toggle and grows out of that corner exactly as emcn's
 * Radix surfaces do (`dropdown-menu.tsx`: `fade-in-0 zoom-in-95`).
 *
 * Animations rather than transitions: an animation runs from mount, so the card needs
 * no hidden "from" frame and no `requestAnimationFrame` to step into — rAF is throttled
 * in an unfocused window, which would strand the card mounted-but-invisible.
 *
 * `duration-150` must match {@link PEEK_EXIT_DURATION_MS}.
 */
const PEEK_CARD_ENTER = cn(
	PEEK_CARD_CHROME,
	"animate-in fade-in-0 zoom-in-95 duration-150 ease-out motion-reduce:animate-none",
);
const PEEK_CARD_EXIT = cn(
	PEEK_CARD_CHROME,
	"pointer-events-none animate-out fade-out-0 zoom-out-95 fill-mode-forwards duration-150 ease-out motion-reduce:animate-none",
);

interface WorkspaceChromeProps {
	children: ReactNode;
	/**
	 * The rail this chrome hosts. Rendered once inside the shell and never re-mounted
	 * across collapse, peek, or fullscreen; it reads collapse and peek state through
	 * {@link useSidebarChrome}. The application passes the shell's `Sidebar`, whose
	 * middle is whichever module the reader is in.
	 */
	sidebar: ReactNode;
	/** Cookie-derived collapse state from the server layout; seeds the sidebar's first render. */
	initialSidebarCollapsed?: boolean;
	/**
	 * An overlay with a full-screen scrim is open, so the desktop peek card yields
	 * to it. A prop rather than a read of the Agent's search-palette store: that
	 * store imports the whole block registry, and this frame is now on the panel's
	 * eager path.
	 */
	overlayOpen?: boolean;
}

/** Chromium Navigation API slice (absent from TS lib.dom). */
type ChromiumNavigation = EventTarget & { canGoBack: boolean; canGoForward: boolean };

const LANE_NAV_BUTTON =
	"flex size-[var(--desktop-title-bar-control-size)] items-center justify-center rounded-lg transition-colors disabled:pointer-events-none disabled:opacity-40 hover-hover:bg-[var(--surface-active)]";
const LANE_NAV_ICON = "size-[var(--desktop-title-bar-control-icon-size)] text-[var(--text-icon)]";

/**
 * Back/forward history arrows in the desktop title-bar lane, right of the
 * sidebar toggle. Only the macOS shell sets the inset attribute, so the web
 * app never shows them; the shell's renderer is Chromium, so the Navigation
 * API is always there for the arrow state.
 */
function TitleBarHistoryNav() {
	const t = useT();
	const [can, setCan] = useState({ back: false, forward: false });

	useEffect(() => {
		const nav = (window as { navigation?: ChromiumNavigation }).navigation;
		if (!nav) return;
		let disposed = false;
		// currententrychange dispatches SYNCHRONOUSLY from the history mutation
		// that caused it, which can originate inside another component's
		// useInsertionEffect (style libraries navigate during commit). Scheduling
		// state there is forbidden ("useInsertionEffect must not schedule
		// updates"), so the update defers to a microtask, which flushes after
		// the commit's synchronous work unwinds.
		const sync = () => {
			queueMicrotask(() => {
				if (!disposed) setCan({ back: nav.canGoBack, forward: nav.canGoForward });
			});
		};
		sync();
		nav.addEventListener("currententrychange", sync);
		return () => {
			disposed = true;
			nav.removeEventListener("currententrychange", sync);
		};
	}, []);

	return (
		<div className="absolute top-[var(--desktop-title-bar-control-offset)] left-[calc(var(--desktop-title-bar-inset-x)+var(--desktop-title-bar-control-size)+4px)] z-30 hidden h-[var(--desktop-title-bar-control-size)] items-center gap-[2px] [-webkit-app-region:no-drag] [[data-agent-desktop-title-bar=inset]_&]:flex">
			<button
				type="button"
				aria-label={t("Back")}
				disabled={!can.back}
				onClick={() => window.history.back()}
				className={LANE_NAV_BUTTON}
			>
				<ArrowLeft className={LANE_NAV_ICON} />
			</button>
			<button
				type="button"
				aria-label={t("Forward")}
				disabled={!can.forward}
				onClick={() => window.history.forward()}
				className={LANE_NAV_BUTTON}
			>
				<ArrowRight className={LANE_NAV_ICON} />
			</button>
		</div>
	);
}

/**
 * Renders the app chrome as a single persistent tree — the workspace layout and the
 * organization layout both mount it, each with its own sidebar. Because this
 * component lives in the layout it persists across navigations, so the rail
 * never re-mounts.
 *
 * The docked rail and content pane share one width transition. Drag-resizing,
 * hydration, and reduced motion bypass it; the floating peek retains its own
 * enter/exit animation.
 *
 * On the macOS desktop shell, where collapsing hides the rail entirely, the same
 * wrapper doubles as the hover-peek card: hovering the title-bar sidebar toggle
 * takes it out of flow, floats it over the content inset from the window edge, and
 * re-points `--sidebar-width` at the restore width. The sidebar is never re-mounted
 * or duplicated for this — see {@link useSidebarPeek}.
 */
export function WorkspaceChrome({
	children,
	sidebar,
	initialSidebarCollapsed = false,
	overlayOpen = false,
}: WorkspaceChromeProps) {
	const t = useT();
	const pathname = useLocation().pathname;

	const storeIsCollapsed = useSidebarStore((s) => s.isCollapsed);
	const hasHydrated = useSidebarStore((s) => s._hasHydrated);
	const syncSidebarWidth = useSidebarStore((s) => s.syncWidth);
	const toggleSidebar = useSidebarStore((s) => s.toggleCollapsed);

	/**
	 * Single source of collapse for the whole chrome, driving the rail's structure,
	 * labels, and width. The server renders from the `sidebar_collapsed` cookie
	 * (`initialSidebarCollapsed`) and the store seeds from the same cookie — after
	 * the pre-paint script migrates any legacy `localStorage` flag — so prop and
	 * store agree. The prop is used until the store hydrates (keeping the first
	 * client render identical to the server), then the store takes over.
	 */
	const isCollapsed = hasHydrated ? storeIsCollapsed : initialSidebarCollapsed;

	/**
	 * The hover-peek only exists where collapsing leaves nothing behind — the macOS
	 * desktop shell. The web app keeps a 51px icon rail (with its own hover flyouts),
	 * and native fullscreen falls back to that same rail.
	 */
	const peekEnabled = false;
	const { isPeekActive, isPeekOpen, cardRef, triggerRef, onTriggerEnter, onTriggerLeave } = useSidebarPeek(
		peekEnabled,
		overlayOpen,
	);

	// Hydrate the persisted width before paint (collapse comes from the cookie/prop).
	useLayoutEffect(() => {
		void useSidebarStore.persist.rehydrate();
	}, []);

	// Re-apply the sidebar width whenever this persistent shell sees a navigation.
	// The blocking script in the document head only runs on full page loads and
	// store rehydration only fires once, so a soft navigation can leave
	// `--sidebar-width` stuck at its `0px` default — collapsing the sidebar to
	// nothing with no reachable control to bring it back. Re-syncing here recovers
	// that state. Gated on hydration so it never clobbers the persisted value with
	// store defaults during the pre-hydration window.
	// biome-ignore lint/correctness/useExhaustiveDependencies: `pathname` is the trigger, not an input — the effect exists to re-run on navigation.
	useEffect(() => {
		if (hasHydrated) syncSidebarWidth();
	}, [pathname, hasHydrated, syncSidebarWidth]);

	// Re-clamp the width when the window shrinks below what the persisted width
	// allows, so the sidebar can never grow wider than the viewport permits.
	useEffect(() => {
		let rafId: number | null = null;
		const onResize = () => {
			if (rafId !== null) return;
			rafId = requestAnimationFrame(() => {
				rafId = null;
				syncSidebarWidth();
			});
		};
		window.addEventListener("resize", onResize);
		return () => {
			if (rafId !== null) cancelAnimationFrame(rafId);
			window.removeEventListener("resize", onResize);
		};
	}, [syncSidebarWidth]);

	return (
		<div
			className="desktop-workspace-window-frame relative flex min-h-0 min-w-0 flex-1"
			data-sidebar-collapsed={isCollapsed || undefined}
		>
			<div
				aria-hidden
				className={cn(
					"desktop-window-drag-region desktop-workspace-window-drag-region",
					isCollapsed ? "h-[var(--desktop-title-bar-height)]" : "h-2",
				)}
			/>
			<div
				ref={cardRef}
				className={cn(
					"sidebar-shell-outer shrink-0 overflow-hidden",
					hasHydrated &&
						!isPeekActive &&
						"transition-[width] duration-175 ease-[cubic-bezier(0.25,0.1,0.25,1)] data-[resizing]:transition-none motion-reduce:transition-none",
					isPeekActive ? (isPeekOpen ? PEEK_CARD_ENTER : PEEK_CARD_EXIT) : "w-[var(--sidebar-width)]",
				)}
				data-collapsed={isCollapsed || undefined}
				data-peek={isPeekActive || undefined}
				/* Also hidden mid-exit, where the card is mounted but invisible. */
				aria-hidden={(isPeekActive && !isPeekOpen) || undefined}
				suppressHydrationWarning
			>
				<div
					className={cn(
						"sidebar-shell-inner h-full shrink-0 [&_.sidebar-container]:w-full!",
						isPeekActive ? "w-[var(--sidebar-width)]" : "w-full",
					)}
				>
					<SidebarChromeProvider isCollapsed={isCollapsed} isPeeking={isPeekActive}>
						{sidebar}
					</SidebarChromeProvider>
				</div>
			</div>
			<div
				className="workspace-content-shell flex min-w-0 flex-1 flex-col"
				data-sidebar-collapsed={isCollapsed || undefined}
			>
				<div className="workspace-content-pane flex-1 overflow-hidden bg-[var(--bg)]">{children}</div>
			</div>
			{
				/**
				 * The hover region lives on this wrapper, not the button: `SidebarTooltip`
				 * returns bare `children` when disabled, so suppressing the tooltip while
				 * peeking swaps the element tree and remounts the button. A ref on the
				 * button would go null mid-hover and retract the card; the wrapper never
				 * unmounts, so it stays a stable anchor for the pointer hit-test.
				 */
				// biome-ignore lint/a11y/noStaticElementInteractions: a pointer hit-test region for the desktop peek, with no behaviour of its own to expose.
				<div
					role="presentation"
					ref={triggerRef}
					onMouseEnter={onTriggerEnter}
					onMouseLeave={onTriggerLeave}
					className="absolute top-[var(--desktop-title-bar-control-offset)] left-[var(--desktop-title-bar-inset-x)] z-30 hidden size-[var(--desktop-title-bar-control-size)] [-webkit-app-region:no-drag] [[data-agent-desktop-title-bar=inset]_&]:block"
				>
					<>
						<button
							type="button"
							onClick={toggleSidebar}
							className="flex size-full items-center justify-center rounded-lg transition-colors hover-hover:bg-[var(--surface-active)]"
							aria-label={isCollapsed ? t("Expand sidebar") : t("Collapse sidebar")}
						>
							<PanelLeft className="size-[var(--desktop-title-bar-control-icon-size)] text-[var(--text-icon)]" />
						</button>
					</>
				</div>
			}
			<TitleBarHistoryNav />
		</div>
	);
}
